/**
 * Presentation-only helper for Markdown tables where one summary row shares a
 * header with many detail rows but populates disjoint column groups (the
 * evolution diff table is the motivating case). Splits such a table into one
 * table per populated-column group, omitting em-dash-only columns within each
 * group. Leaves every other table byte-identical.
 */

const EMPTY_CELL = /^[\s—–-]*$/;

function isEmptyCell(cell: string): boolean {
  return EMPTY_CELL.test(cell.trim());
}

function splitTableRow(line: string): string[] {
  let inner = line.trim();
  if (inner.startsWith("|")) inner = inner.slice(1);
  if (inner.endsWith("|")) inner = inner.slice(0, -1);
  return inner.split("|").map((c) => c.trim());
}

function isSeparatorRow(cells: string[]): boolean {
  return cells.length > 0 && cells.every((c) => /^:?-{3,}:?$/.test(c));
}

function parseMarkdownTable(
  lines: readonly string[],
): { header: string[]; rows: string[][] } | null {
  if (lines.length < 2) return null;
  const header = splitTableRow(lines[0]!);
  const sep = splitTableRow(lines[1]!);
  if (!isSeparatorRow(sep) || header.length !== sep.length) return null;
  const rows = lines
    .slice(2)
    .map(splitTableRow)
    .filter((r) => r.length > 0);
  if (rows.some((r) => r.length !== header.length)) return null;
  return { header, rows };
}

function rowPopulatedCols(row: string[]): number[] {
  const cols: number[] = [];
  for (let i = 0; i < row.length; i++) {
    if (!isEmptyCell(row[i] ?? "")) cols.push(i);
  }
  return cols;
}

/**
 * Group columns that are populated together by at least one row.
 *
 * Grouping on *identical* row signatures is too strict for real data: two
 * detail rows describing the same kind of thing routinely differ in which
 * optional column they fill (one names a workload, the next has none), which
 * would put them in separate tables and defeat the purpose. Columns that
 * co-occur in any row belong to the same group, so a row's optional cells join
 * it to its own group rather than splitting it off.
 */
function groupColumnsByCoOccurrence(rows: string[][], exclude: Set<number>): number[][] {
  const parent = new Map<number, number>();
  const find = (c: number): number => {
    let root = parent.get(c) ?? c;
    while (root !== (parent.get(root) ?? root)) root = parent.get(root) ?? root;
    return root;
  };
  const union = (a: number, b: number): void => {
    const [ra, rb] = [find(a), find(b)];
    if (ra !== rb) parent.set(ra, rb);
  };

  for (const row of rows) {
    const cols = rowPopulatedCols(row).filter((c) => !exclude.has(c));
    for (const col of cols) if (!parent.has(col)) parent.set(col, col);
    for (let i = 1; i < cols.length; i++) union(cols[0]!, cols[i]!);
  }

  const groups = new Map<number, number[]>();
  for (const col of parent.keys()) {
    const root = find(col);
    groups.set(root, [...(groups.get(root) ?? []), col]);
  }
  return [...groups.values()]
    .map((g) => g.sort((a, b) => a - b))
    .sort((a, b) => a[0]! - b[0]!);
}

function splitSparseTable(
  header: string[],
  rows: string[][],
): { header: string[]; rows: string[][] }[] | null {
  if (rows.length < 2) return null;

  // Columns every row fills carry the same meaning for all of them (a basis, a
  // confidence), so they are repeated in each part rather than assigned to one.
  const shared = new Set(
    header
      .map((_, col) => col)
      .filter((col) => rows.every((row) => !isEmptyCell(row[col] ?? ""))),
  );

  const groups = groupColumnsByCoOccurrence(rows, shared);
  if (groups.length < 2) return null;

  // A row spanning two groups has no single part to belong to; splitting would
  // have to drop or duplicate its cells, so the table is left as it is.
  const groupOf = new Map<number, number>();
  groups.forEach((group, index) => group.forEach((col) => groupOf.set(col, index)));
  const rowGroup = rows.map((row) => {
    const indices = new Set(
      rowPopulatedCols(row)
        .filter((c) => !shared.has(c))
        .map((c) => groupOf.get(c)),
    );
    return indices.size === 1 ? [...indices][0]! : -1;
  });
  if (rowGroup.some((g) => g < 0)) return null;

  const parts = groups.map((group, index) => {
    const colIndices = [...new Set([...shared, ...group])].sort((a, b) => a - b);
    return {
      header: colIndices.map((i) => header[i]!),
      rows: rows.filter((_, i) => rowGroup[i] === index).map((r) => colIndices.map((i) => r[i]!)),
    };
  });
  return parts.every((p) => p.rows.length > 0) ? parts : null;
}

function renderMarkdownTable(header: string[], rows: string[][]): string[] {
  const sep = header.map(() => "---");
  return [
    `| ${header.join(" | ")} |`,
    `| ${sep.join(" | ")} |`,
    ...rows.map((r) => `| ${r.join(" | ")} |`),
  ];
}

function isTableStart(lines: readonly string[], index: number): boolean {
  if (index + 1 >= lines.length) return false;
  const header = splitTableRow(lines[index]!);
  const sep = splitTableRow(lines[index + 1]!);
  return header.length > 1 && isSeparatorRow(sep) && header.length === sep.length;
}

function findTableEnd(lines: readonly string[], start: number): number {
  let end = start + 2;
  while (end < lines.length) {
    const line = lines[end]!;
    if (!line.trim()) break;
    if (!line.includes("|")) break;
    end++;
  }
  return end;
}

/** Split sparse merged tables in `markdown`; other content is unchanged. */
export function splitSparseMarkdownTables(markdown: string): string {
  const lines = markdown.split("\n");
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    if (isTableStart(lines, i)) {
      const end = findTableEnd(lines, i);
      const tableLines = lines.slice(i, end);
      const parsed = parseMarkdownTable(tableLines);
      const split = parsed ? splitSparseTable(parsed.header, parsed.rows) : null;
      if (split && split.length > 1) {
        for (let p = 0; p < split.length; p++) {
          if (p > 0) out.push("");
          out.push(...renderMarkdownTable(split[p]!.header, split[p]!.rows));
        }
      } else {
        out.push(...tableLines);
      }
      i = end;
      continue;
    }
    out.push(lines[i]!);
    i++;
  }
  return out.join("\n");
}

/** @internal Exported for unit tests. */
export const _testing = {
  splitSparseTable,
  parseMarkdownTable,
  isEmptyCell,
};
